import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Input, NgModule, Output, QueryList, ContentChildren, ElementRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { WFieldModule } from '@wipo/w-angular/w-field';
import { WInlineFilterModule } from '@wipo/w-angular/w-inline-filter';
import { WSidebarSectionComponent } from './w-sidebar-section.component';
import { IterationOscillationDamper, WAdaptiveBase, maxZIndex, observableString } from '@wipo/w-angular/shared';
import { i18n } from '@wipo/w-angular/internal';
import { Observable, fromEvent, merge, of } from 'rxjs';
import { WButtonModule } from '@wipo/w-angular/w-button';
import { debounceTime, filter, takeUntil } from 'rxjs/operators';


interface SectionTab {
  labelAsync: Observable<string>;
  section: WSidebarSectionComponent;
}
@Component({
  selector: 'w-sidebar',
  templateUrl: './w-sidebar.component.html',
  host: {
    class: 'b-sidebar-host'
  },
  standalone: false
})
/*
@id w-sidebar
@name w-sidebar
@status release
@description
*/
export class WSidebarComponent extends WAdaptiveBase implements AfterViewInit {
  @ContentChildren(WSidebarSectionComponent) private sections!: QueryList<WSidebarSectionComponent>;
  @ViewChild('space') private space!: ElementRef<HTMLDivElement>;
  @ViewChild('contentSpace') private contentSpace!: ElementRef<HTMLDivElement>;
  @ViewChild('wrapper') private wrapper!: ElementRef<HTMLElement>;
  @ViewChild('content') private content!: ElementRef<HTMLElement>;

  /* alternative format for mobile - tabbed */
  @Input() showTabs: boolean = false; // show tabbed format in mobile, include Apply and Cancel buttons (each section gets updated separately)
  @Input() tabCancel: string | Observable<string> = of(i18n.label.cancel); // label of Cancel button
  get tabCancelAsync() {
    return observableString(this.tabCancel);
  }
  @Input() tabApply: string | Observable<string> = of(i18n.label.apply);// label of Apply button
  get tabApplyAsync() {
    return observableString(this.tabApply);
  }
  @Output() wTabApply = new EventEmitter<void>();   // emits on apply
  @Output() wTabCancel = new EventEmitter<void>();  // emits on cancel
  selectedTab?: number;
  get showTabbed() {
    return this.isMobile && this.showTabs;
  }

  @Input() title: string | Observable<string> = of(i18n.label.filters); // name in sidebar header
  get titleAsync() {
    return observableString(this.title);
  }
  @Input() options?: string;
  @Input() isCollapsed = true;
  @Input() showPopup: boolean = false;
  @Input() stickyAt?: number; // filter wrapper floats below sticky bar

  /** make sure to stick only if parent top is higher (undo sticky when scrolled back) */
  private get isSticky() {
    return typeof this.stickyAt === 'number' && this.elementRef.nativeElement.getBoundingClientRect().top < this.stickyAt && !this.isMobile;
  }
  get stickyTop(): number {
    return this.isSticky ? (this.stickyAt ?? 0) : 0;
  };
  stateSticky = new EventEmitter<boolean>(true);
  stateBarHeight = new EventEmitter<number>(true);

  maxZIndex = 0; // check for z-index when making sticky

  @Output() isCollapsedChange = new EventEmitter<boolean>();
  @Output() showPopupChange = new EventEmitter<boolean>();
  @Output() isChildrenCollapsedChange = new EventEmitter<void>();

  tabs: SectionTab[] = [];

  constructor(protected elementRef: ElementRef<HTMLElement>, protected cdr: ChangeDetectorRef) {
    super();
  }
  ngAfterViewInit(): void {
    this.baseElement = this.wrapper.nativeElement;

    // close if any of any apply or cancel clicked
    merge(this.wTabApply, this.wTabCancel).pipe(takeUntil(this.destruction)).subscribe(() => {
      this.isCollapsed = true;
    })
    // check filter sections, recreate and re-initialize tabs
    merge(this.bs.mobile, this.sections.changes).pipe(takeUntil(this.destruction)).subscribe(() => {
      this.iterateTabs();
    })
    this.iterateTabs();
    /** make sure resize iterations converge */
    const iterationConverger = new IterationOscillationDamper();
    /* watch changes in size or position and scroll events */
    /** list for scroll from window */
    const scroll$ = fromEvent(window, 'scroll');
    /** listen for scroll from any scrollable parent containers */
    const scrolls = getScrollableAncestors(this.elementRef.nativeElement).map(el => fromEvent(el, 'scroll'));
    const trigger = merge(this.elementPosition, this.isCollapsedChange, this.isChildrenCollapsedChange, scroll$, ...scrolls, this.stateResize)
      .pipe(
        filter(() => !this.isMobile),
        takeUntil(this.destruction),
        debounceTime(30)
      );
    trigger.subscribe(() => {
      this.adjustHeight(iterationConverger);
    });
    this.listenForSections();
    this.sections.changes.pipe(takeUntil(this.destruction)).subscribe(() => {
      /** we stop previous listener and start a new one each time sections change */
      this.listenForSections();
    });
    this.resizeObserver.observe(this.space?.nativeElement);
    this.resizeObserver.observe(this.contentSpace?.nativeElement);
  }
  private listenForSections() {
    /** listen for children changes */
    merge(...this.sections.map(s => s.isCollapsedChange)).pipe(
      takeUntil(this.destruction),
      takeUntil(this.sections.changes),
      debounceTime(30),
    ).subscribe(() => {
      this.isChildrenCollapsedChange.emit();
    });
  }
  toggle() {
    this.isCollapsed = !this.isCollapsed;
    this.showPopup = false;
    this.isCollapsedChange.emit(this.isCollapsed);
    this.showPopupChange.emit(this.showPopup);
  }
  /** bar height */
  public adjustBarHeight() {
    this.stateSticky.next(this.isSticky);
    /** calculate bar height from content height */
    const wrapperHeight = getHeight(this.wrapper) - getHeight(this.space);
    const contentHeight = getHeight(this.content) - getHeight(this.contentSpace);
    const barHeight = Math.max(wrapperHeight, contentHeight);
    this.stateBarHeight.next(barHeight);
    setTimeout(() => {
      if (this.isSticky) {
        const zIndex = maxZIndex();
        if (zIndex > this.maxZIndex) {
          this.maxZIndex = zIndex + 1;
        }
      } else {
        this.maxZIndex = 0;
      }
    });
  }
  /** adjust height of sidebar panel if whatever follows not reach bottom of the page */
  private adjustHeight(iterations: IterationOscillationDamper) {
    this.adjustBarHeight();
    /** this._el is filter wrapper element */
    const rect = this._el.getBoundingClientRect();
    const pageHeight = window.innerHeight;
    const parent = this.elementRef.nativeElement.parentElement;
    if (!parent) {
      // no parent, nothing to do (should have one)
      this.setBarHeight(rect.height);
      return;
    }
    const parentRect = parent.getBoundingClientRect();
    const parentHeight = parent.clientHeight;

    /** body height, adjusting for borders */
    const borders = parseInt(getComputedStyle(document.body).borderTopWidth, 10) + parseInt(getComputedStyle(document.body).borderBottomWidth, 10);
    const bodyHeight = document.body.scrollHeight + borders; // adjust for borders
    // can extend this much or shrink if negative
    const extendSpace = pageHeight - bodyHeight - parentScrolledHeight(this._el);
    // already extended this much
    const space = this.space?.nativeElement.clientHeight ?? 0; // can shrink this much

    if (this.isSticky) {
      /** in sticky mode size fixed */
      iterations.reset();
      /** in sticky mode: from stickAt to parent bottom */
      const stickyAt = this.stickyAt ?? 0;
      const toParent = parentRect.top - stickyAt + parentHeight;
      /** at least to parent bottom, no larger that remaining page height */
      const filterHeight = this._el.scrollHeight;
      const remainingSpace = pageHeight - stickyAt;
      const filterAdjusted = filterHeight - space + 2;
      const barHeight = Math.min(Math.max(toParent, filterAdjusted), remainingSpace);
      this.setBarHeight(barHeight);
    } else if (iterations.canCalculate) {
      // try to push sidebar to bottom
      // min height (not extended)
      const height = rect.height;
      const base = Math.ceil(height - space);
      // this min-height will push bar as big ar possible (leaving space for any footer below it)
      const minHeight = Math.ceil(iterations.calculateNextSize(height, extendSpace, base, true, false));
      this.setBarHeight(minHeight);
    }
  }
  private setBarHeight(height?: number) {
    this.elementRef.nativeElement.style.setProperty('--w-sidebar-height', height === undefined ? '' : `${height}px`);
  }
  private iterateTabs() {
    setTimeout(() => {
      this.tabs = this.sections.reduce((tt, section) => {
        if (this.showTabs && this.isMobile) {
          if (this.sections.length > 1) {
            tt.push({ labelAsync: observableString(section.title ?? '-'), section });
            section.hiddenTab = true;
          }
          section.showInTab = true;
        } else {
          section.showInTab = false;
          section.hiddenTab = false;
        }
        return tt;
      }, [] as SectionTab[]);
    });
  }
  /* call this to display particular tab */
  showTab(tab: SectionTab, i: number) {
    this.selectedTab = i;
    this.sections.forEach((s, i) => {
      s.hiddenTab = tab.section !== s;
    });
  }
}
const parentScrolledHeight = (el: HTMLElement) => {
  let parent = el.parentElement;
  let height = 0;
  if (parent) {
    height += parent.scrollHeight - parent.clientHeight + parentScrolledHeight(parent);
  }
  return height;
}
const getScrollableAncestors = (element: HTMLElement): HTMLElement[] => {
  const scrollableAncestors: HTMLElement[] = [];
  let parent = element.parentElement;

  while (parent) {
    const overflowY = window.getComputedStyle(parent).overflowY;
    if (overflowY === 'auto' || overflowY === 'scroll') {
      scrollableAncestors.push(parent);
    }
    parent = parent.parentElement;
  }
  return scrollableAncestors;
}
const getHeight = (elementRef?: ElementRef) => {
  return elementRef?.nativeElement.getBoundingClientRect().height ?? 0
}

@NgModule({
  imports: [CommonModule, FormsModule, WFieldModule, WInlineFilterModule, WButtonModule],
  exports: [WSidebarComponent, WSidebarSectionComponent],
  declarations: [WSidebarComponent, WSidebarSectionComponent]
})
export class WSidebarModule { }
